import { useQuery, keepPreviousData } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * Customer activity — two endpoints:
 *
 * 1. `GET /customers/:id/activity-logs` — per-customer activity timeline
 *    (the legacy "Lịch sử hoạt động"). Field names mirror the backend exactly.
 *    `metadata` is either null or a `{ old, new }` field-diff.
 *
 * 2. `GET /customers/calendar-events` — cross-customer birthday / founding-day
 *    list scoped to the signed-in employee (backend applies auth scope).
 *    Used in the Account module "Customer Events" page.
 */

/** A `{ old, new }` field-diff snapshot, or null when the action carries no diff. */
export interface ActivityMetadataDiff {
  old?: Record<string, unknown> | null;
  new?: Record<string, unknown> | null;
}

export interface ActivityLogRow {
  log_id: number;
  log_type: string;
  action_type: string;
  description: string;
  metadata: ActivityMetadataDiff | null;
  employee_id: number | null;
  employee_name: string;
  created_at: string;
  customer_id: number;
  ref_id: number | null;
}

export interface ActivityLogsParams {
  page?: number;
  page_size?: number;
  /** ISO lower bound (inclusive) for `created_at`. */
  gte?: string;
  /** ISO upper bound (inclusive) for `created_at`. */
  lte?: string;
}

function activityQuery(params: ActivityLogsParams): string {
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  return qs.stringify(q);
}

/** `GET /customers/:id/activity-logs` — the activity timeline for one customer. */
export function useCustomerActivityLogs(id: number, params: ActivityLogsParams = {}) {
  return useQuery({
    queryKey: ['customers', 'activity-logs', id, params],
    enabled: !!id,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ResponseArray<ActivityLogRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ActivityLogRow>>(
        `/customers/${id}/activity-logs?${activityQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Customer calendar events ───────────────────────────────────────────── */

export type CustomerCalendarEventType = 'birthday' | 'founding';

/**
 * One birthday / founding-day row as returned by the backend.
 *
 * `followers` contains employee_id numbers (not names). The backend strips the
 * JSC sentinel employee (id 30) before returning. `company_name` is null for
 * birthday rows; always present for founding rows.
 */
export interface CustomerCalendarEvent {
  event_type: CustomerCalendarEventType;
  customer_id: number;
  customer_name: string;
  company_name: string | null;
  event_date: string;
  event_day: number;
  /** Employee IDs of the assigned sale/CS followers. */
  followers: number[];
  contact_names: string[];
}

export interface CalendarEventsParams {
  month: number;
  page: number;
  page_size: number;
  /** Employee IDs to filter by sale follower — only sent when the role guard allows it. */
  sale_follower_ids?: number[];
  /** Employee IDs to filter by CS follower — only sent when the role guard allows it. */
  cs_follower_ids?: number[];
}

/**
 * `GET /customers/calendar-events?month=&page=&page_size=&sale_follower_ids=&cs_follower_ids=`
 *
 * The backend auto-scopes the result to the signed-in employee. The follower
 * filter params are only passed when the caller has the
 * `can_filter_customer_event_followers` permission (legacy role guard — check
 * `/auth/me` response field of same name).
 */
export function useCustomerCalendarEvents(params: CalendarEventsParams) {
  return useQuery({
    queryKey: ['customers', 'calendar-events', params],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ResponseArray<CustomerCalendarEvent>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerCalendarEvent>>(
        `/customers/calendar-events?${qs.stringify(params, { arrayFormat: 'none' })}`,
      );
      return res.data;
    },
  });
}
